/**
 * Validador de teléfono chileno (FEAT-069).
 *
 * Formato canónico: `+56XXXXXXXXX` (12 caracteres exactos):
 *  - Prefijo país `+56` obligatorio.
 *  - 9 dígitos posteriores.
 *  - Móviles (`9XXXXXXXX`) y fijos (`2XXXXXXXX`, etc.) aceptados — la utilidad
 *    no distingue entre tipos (la regla SII / DTE / WhatsApp acepta ambos
 *    siempre que respeten longitud y prefijo país).
 *
 * Helpers:
 *  - `cleanChileanPhone(value)`: remueve espacios, guiones, paréntesis y puntos.
 *    Normaliza prefijos comunes: `56XXXXXXXXX` → `+56XXXXXXXXX`, `9XXXXXXXX`
 *    (sin prefijo país, 9 dígitos) → `+569XXXXXXXX`.
 *  - `validateChileanPhone(value)`: true si el valor (post-`cleanChileanPhone`)
 *    cumple el formato `+56XXXXXXXXX`.
 *  - `formatChileanPhone(value)`: retorna el valor normalizado canónico, o el
 *    input original si no es normalizable a formato válido.
 *
 * Establece el formato estándar transversal para todo input de teléfono
 * chileno en el sistema (onboarding billing, perfil, attendees, etc.).
 */

const CHILEAN_PHONE_REGEX = /^\+56[0-9]{9}$/;
const CHILEAN_PHONE_LOCAL_LENGTH = 9;

/**
 * Limpia un teléfono chileno removiendo separadores comunes (espacios,
 * guiones, paréntesis, puntos) y normalizando el prefijo país.
 *
 * Reglas de normalización:
 *  - `+56XXXXXXXXX` → se mantiene tal cual.
 *  - `56XXXXXXXXX`  → se prepende `+`.
 *  - `XXXXXXXXX` (9 dígitos sin prefijo país) → se prepende `+56`.
 *  - Cualquier otro patrón → se retorna el string limpio sin prefijo automático.
 */
export function cleanChileanPhone(value: string): string {
  if (!value) return "";
  // Eliminar separadores visuales: espacios, guiones, paréntesis y puntos.
  const stripped = value.replace(/[\s\-().]/g, "");
  if (stripped.startsWith("+56")) return stripped;
  if (stripped.startsWith("56") && stripped.length === CHILEAN_PHONE_LOCAL_LENGTH + 2) {
    return `+${stripped}`;
  }
  if (/^[0-9]{9}$/.test(stripped)) return `+56${stripped}`;
  return stripped;
}

/**
 * Valida un teléfono chileno en formato canónico `+56XXXXXXXXX`.
 *
 * Acepta también entradas con separadores comunes (`+56 9 1234 5678`,
 * `(56) 9-1234-5678`) gracias a la normalización previa con
 * `cleanChileanPhone`.
 *
 * @example
 * validateChileanPhone("+56912345678") // true
 * validateChileanPhone("+56 9 1234 5678") // true
 * validateChileanPhone("912345678") // true (auto-prepend +56)
 * validateChileanPhone("12345678") // false (longitud incorrecta)
 * validateChileanPhone("+1234567890") // false (prefijo país incorrecto)
 */
export function validateChileanPhone(value: string): boolean {
  if (typeof value !== "string") return false;
  return CHILEAN_PHONE_REGEX.test(cleanChileanPhone(value));
}

/**
 * Formatea un teléfono chileno al formato canónico `+56XXXXXXXXX`. Si el input
 * no es normalizable a un teléfono chileno válido, retorna el input original
 * sin modificaciones (defensivo: nunca lanza ni corrompe data inválida).
 *
 * @example
 * formatChileanPhone("9 1234 5678") // "+56912345678"
 * formatChileanPhone("56912345678") // "+56912345678"
 * formatChileanPhone("+56 9 1234 5678") // "+56912345678"
 * formatChileanPhone("not-a-phone") // "not-a-phone"
 */
export function formatChileanPhone(value: string): string {
  if (!value) return "";
  const clean = cleanChileanPhone(value);
  return CHILEAN_PHONE_REGEX.test(clean) ? clean : value;
}
